import { NextRequest } from 'next/server';
import buildShopSubdomainUrl from '../shopSubdomain';
import { CartAttribute, ShopifyVariant } from '@/lib/shopify';

export const legacyHost =
  process.env.NEXT_PUBLIC_BACKEND_DOMAIN || 'lovevery.com';

export function getShopifySubflowUrl(req: NextRequest) {
  const path = req.nextUrl.pathname.replace('-v3', '');
  const locale = req.nextUrl.locale;
  const shopifySubFlowUrl = buildShopSubdomainUrl(path, locale);
  return shopifySubFlowUrl;
}

export function getDefaultVariant(
  productTitle: string,
  variants: ShopifyVariant[],
): ShopifyVariant {
  // if product "Organic Cotton Baby Doll", use "Tan Skin Tone" variant by default
  const organicCottonBabyDollTitle = 'Organic Cotton Baby Doll';
  const organicCottonBabyDollDefaultIndex = 1; // "Tan Skin Tone" index

  if (productTitle === organicCottonBabyDollTitle) {
    return variants?.[organicCottonBabyDollDefaultIndex] || variants[0];
  }

  return variants[0];
}

export function convertCartLineItemPropertiesToAttributes(
  properties?: Record<string, string | null>,
): CartAttribute[] {
  if (!properties) return [];

  return (
    Object.entries(properties)
      // Using a type predicate to filter out null values. This tells TypeScript
      // that after filtering, each entry is of type [string, string].
      .filter((entry): entry is [string, string] => entry[1] !== null)
      .map(([key, value]) => ({ key, value }))
  );
}

export function getCartLineItemProperty(
  targetAttributeKey: string,
  attributes?: CartAttribute[],
  settings?: { parseJson: boolean },
) {
  if (!attributes) {
    return null;
  }

  // Create a set of acceptable keys: original and underscore-prefixed.
  // note: this underscore prefixing happens when a user visits the cart page.
  // It supports ensuring line item properties are hidden from the ui on the shopify checkout page.
  const keySet = new Set([targetAttributeKey, `_${targetAttributeKey}`]);

  const targetAttribute = attributes.find((attribute) => {
    return keySet.has(attribute.key);
  });

  let targetValue = targetAttribute?.value;

  // If parsing is enabled and the targetValue is a string, attempt to parse it as JSON.
  if (settings?.parseJson && targetValue && !_isObject(targetValue)) {
    try {
      targetValue = JSON.parse(targetValue);
    } catch (error) {
      console.error(
        `Failed to parse attribute ${targetAttributeKey} as json`,
        targetValue,
        error,
      );
    }
  }

  return targetValue;
}

function _isObject(data: any) {
  // Checks if data is an object (excluding arrays and null).
  return typeof data === 'object' && data !== null && !Array.isArray(data);
}
